import React, { useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  SalonPublicThemeProvider,
  useSalonPublicTheme,
} from '../../../../context/SalonPublicThemeContext';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';
import NeoDermaIdentity from './NeoDermaIdentity';


const NeoDermaTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { t } = useTranslation();
  const { config, layout, tokens: theme } = useSalonPublicTheme();
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const openingHours = Array.isArray(viewModel?.openingHours) ? viewModel.openingHours : [];
  const closures = Array.isArray(viewModel?.closures) ? viewModel.closures : [];
  const categories = Array.isArray(viewModel?.serviceCategories)
    ? viewModel.serviceCategories.filter((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    ))
    : [];
  const tabCategories = categories.filter((category) => (
    category?.id != null && typeof category?.name === 'string' && category.name.trim()
  ));
  const selectedCategoryId = tabCategories.some((category) => (
    String(category.id) === activeCategoryId
  )) ? activeCategoryId : tabCategories[0] ? String(tabCategories[0].id) : null;
  const visibleCategories = selectedCategoryId == null
    ? categories
    : categories.filter((category) => String(category.id) === selectedCategoryId);
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasPracticalInfo = Boolean(
    visibility.opening_hours && (openingHours.length > 0 || closures.length > 0),
  );
  const hasReviews = Boolean(
    reviews.length > 0 || publicState.reviewsLoading || publicState.reviewsError,
  );
  const hasTeam = Boolean(
    visibility.team && viewModel?.team?.some((member) => member?.id != null),
  );
  const hasEditorialInfo = Boolean(
    (visibility.gallery && (photos.length > 0 || mode === 'preview')) || identity.description,
  );
  const hasCatalog = Boolean(categories.length > 0 || publicState.servicesLoading);
  const technicalLayoutConfig = {
    ...layout.config,
    content: {
      ...layout.config.content,
      services: 'grid',
      service_columns: 2,
    },
  };

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="neo_derma"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <NeoDermaIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="neo-derma-scroll-region"
        data-scroll-container="document"
        sx={{
          flex: 'none',
          minHeight: 'auto',
          width: '100%',
          overflowY: 'visible',
          overflowX: 'hidden',
        }}
      >
        <Box
          component="main"
          data-testid="neo-derma-content"
          data-layout={mobile ? 'single-column' : 'technical-clinic'}
          sx={{
            width: '100%',
            maxWidth: 1240,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            py: { xs: 3, md: 5 },
            boxSizing: 'border-box',
          }}
        >
          {hasCatalog ? (
            <Box
              data-testid="neo-derma-treatment-index"
              data-layout={mobile ? 'stacked-index' : 'vertical-index'}
              sx={{
                display: 'grid',
                gridTemplateColumns: !mobile && tabCategories.length > 0
                  ? '230px minmax(0, 1fr)'
                  : 'minmax(0, 1fr)',
                alignItems: 'start',
                gap: { xs: 2, md: 4 },
                p: { xs: 2, md: 4 },
                backgroundColor: theme.salon.surface,
                border: `1px solid ${theme.salon.border}`,
              }}
            >
              {tabCategories.length > 0 ? (
                <Box data-salon-template-block="categories" sx={{ minWidth: 0 }}>
                  <Tabs
                    value={selectedCategoryId}
                    onChange={(_event, value) => setActiveCategoryId(value)}
                    aria-label={t('landing_salon_details.our_services')}
                    orientation={mobile ? 'horizontal' : 'vertical'}
                    variant="scrollable"
                    scrollButtons="auto"
                    selectionFollowsFocus
                    allowScrollButtonsMobile
                    sx={{
                      minHeight: 0,
                      p: 0.75,
                      borderInlineStart: !mobile ? `4px solid ${theme.salon.primary}` : 0,
                      borderBottom: mobile ? `1px solid ${theme.salon.border}` : 0,
                      backgroundColor: theme.salon.surfaceMuted,
                      '& .MuiTabs-indicator': { display: 'none' },
                      '& .MuiTabs-flexContainer': {
                        gap: 0.5,
                        alignItems: mobile ? 'center' : 'stretch',
                      },
                      '& .MuiTab-root': {
                        minHeight: 44,
                        px: 2,
                        color: theme.salon.secondary,
                        alignItems: mobile ? 'center' : 'flex-start',
                        fontWeight: 700,
                        textAlign: 'start',
                        textTransform: 'none',
                      },
                      '& .MuiTab-root.Mui-selected': {
                        color: theme.salon.onSecondary,
                        backgroundColor: theme.salon.secondary,
                      },
                    }}
                  >
                    {tabCategories.map((category) => (
                      <Tab
                        key={category.id}
                        id={`neo-derma-tab-${category.id}`}
                        aria-controls={String(category.id) === selectedCategoryId
                          ? `neo-derma-panel-${category.id}`
                          : undefined}
                        label={category.name}
                        value={String(category.id)}
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : null}

              <SalonPublicThemeProvider
                config={config}
                layoutConfig={technicalLayoutConfig}
                templateKey="neo_derma"
                direction={direction}
              >
                <Box
                  role={selectedCategoryId == null ? undefined : 'tabpanel'}
                  id={selectedCategoryId == null ? undefined : `neo-derma-panel-${selectedCategoryId}`}
                  aria-labelledby={selectedCategoryId == null
                    ? undefined
                    : `neo-derma-tab-${selectedCategoryId}`}
                  sx={{ '& [data-testid="salon-section-services"]': { mt: 0 } }}
                >
                  <SalonTemplateServices
                    data={visibleCategories}
                    variant="technical"
                    mode={mode}
                    viewport={viewport}
                    loading={Boolean(publicState.servicesLoading)}
                    onBookService={actions?.onBookService}
                  />
                </Box>
              </SalonPublicThemeProvider>
            </Box>
          ) : null}
          {hasTeam || hasReviews ? (
            <Box
              data-testid="neo-derma-trust-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                alignItems: 'start',
                gap: { xs: 2, md: 3 },
                mt: { xs: 3, md: 5 },
              }}
            >
              {hasTeam ? (
                <Box data-testid="neo-derma-specialists" sx={{ minWidth: 0 }}>
                  <SalonTemplateTeam
                    data={viewModel?.team}
                    variant="specialists"
                    visible={visibility.team}
                    mode={mode}
                    onSelectMember={actions?.onSelectTeamMember}
                  />
                </Box>
              ) : null}
              {hasReviews ? (
                <SalonTemplateReviews
                  data={{
                    items: reviews,
                    averageRating: viewModel?.averageRating,
                    isAuthenticated: Boolean(publicState.isAuthenticated),
                    isLoading: Boolean(publicState.reviewsLoading),
                    error: publicState.reviewsError || null,
                  }}
                  variant="clinical"
                  visible={visibility.reviews}
                  mode={mode}
                  onOpenReviewComposer={actions?.onOpenReviewComposer}
                  onOpenAllReviews={actions?.onOpenAllReviews}
                />
              ) : null}
            </Box>
          ) : null}
          {hasEditorialInfo ? (
            <Box
              data-testid="neo-derma-information-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                alignItems: 'start',
                gap: { xs: 2, md: 3 },
                mt: { xs: 2, md: 3 },
              }}
            >
              <SalonTemplateGallery
                data={photos}
                salonId={identity.id}
                variant="secondary"
                visible={visibility.gallery}
                mode={mode}
                onSelectPhoto={actions?.onSelectPhoto}
                onNavigatePhoto={actions?.onNavigatePhoto}
                selectedIndex={publicState.photoIndex}
              />
              <SalonTemplateAbout
                data={identity}
                variant="panel"
                visible={Boolean(identity.description)}
              />
            </Box>
          ) : null}
          {hasPracticalInfo || visibility.location ? (
            <Box
              data-testid="neo-derma-practical-grid"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                alignItems: 'start',
                gap: { xs: 2, md: 3 },
                mt: { xs: 3, md: 5 },
                '& [data-testid="salon-section-opening_hours"]': { mt: 0 },
              }}
            >
              {hasPracticalInfo ? (
                <SalonTemplateOpeningHours
                  data={{ hours: openingHours, closures }}
                  variant="compact"
                  visible={visibility.opening_hours}
                  markClosuresAsBlock={false}
                />
              ) : null}
              <SalonTemplateContactLocation
                data={identity}
                variant="compact"
                visible={visibility.location}
                mode={mode}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default NeoDermaTemplate;
